import { useEffect, useState } from "react";
import {
  Bell,
  BookOpen,
  Bookmark,
  Calculator,
  CircleDollarSign,
  CircleDot,
  CreditCard,
  FileText,
  GraduationCap,
  Heart,
  MessageSquareHeart,
  HelpCircle,
  KanbanSquare,
  KeyRound,
  Landmark,
  LayoutDashboard,
  LayoutGrid,
  LineChart,
  Megaphone,
  Newspaper,
  Scale,
  Settings,
  Sparkles,
  UserRound,
  Wallet,
  Receipt,
} from "lucide-react";

import AppSidebar, { type NavSection } from "@/components/AppSidebar";
import { SAVED_FILTERS_CHANGED_EVENT, renameSavedFilter, type SavedFilter } from "@/components/ListingFilters";
import { useAuth } from "@/contexts/AuthContext";
import { api } from "@/lib/api";
import { hasOperatorAccess, isFreeBuyerUser } from "@/lib/access";
import { loadFundingPartners, type FundingPartner } from "@/lib/funding";
import { INTEREST_UNREAD_EVENT } from "@/components/BuyerChats";

/** How often the My Interests unread badge re-checks for advisor replies. */
const INTEREST_UNREAD_POLL_MS = 60_000;

/** The parent nav entry. Each funding company hangs off it as a child. */
const FUNDING_LABEL = "Funding Marketplace";

const BASE_SECTIONS: NavSection[] = [
  {
    heading: "Discover",
    items: [
      // No `module` key: the dashboard is the buyer's landing page, so it is
      // never hidden by the super admin's module-visibility switches — a
      // buyer who could not reach it would land on a page with no way back.
      { label: "Dashboard", to: "/buyer/dashboard", icon: LayoutDashboard },
      { label: "Available Listings", to: "/buyer/home", icon: LayoutGrid },
      { label: "AI Deal Matching", module: "ai_deal_matching", to: "/buyer/deal-scoring", icon: CircleDot, badge: "AI", badgeTone: "ai" },
      { label: "Favorites", module: "favorites", to: "/buyer/favorites", icon: Heart },
      { label: "My Interests", module: "interests", to: "/buyer/interests", icon: MessageSquareHeart },
      // No badge here on purpose: the "New" pill is a super-admin switch now
      // (Module Visibility → New tag), applied in AppSidebar. These three are
      // seeded on, so the sidebar reads exactly as it did before.
      { label: "Deal CRM", module: "deal_crm", to: "/buyer/pipeline", icon: KanbanSquare },
    ],
  },
  {
    heading: "Tools",
    items: [
      { label: "SBA Calculator", module: "sba_calculator", to: "/buyer/sba-calculator", icon: Calculator },
      { label: "Valuation Calculator", module: "valuation_calculator", to: "/buyer/valuation-calculator", icon: Scale },
      { label: "Financial Model", module: "financial_model", to: "/buyer/financial-model", icon: LineChart },
      {
        // A group, not a link: AppSidebar renders an item with `children` as a
        // pure expand/collapse, so `to` here is only the React key. One child
        // per funding company, injected below from GET /funding/partners —
        // adding a company is a backend-only edit, nothing changes here.
        label: FUNDING_LABEL,
        module: "funding_marketplace",
        to: "/buyer/funding",
        icon: CircleDollarSign,
        children: [],
      },
      // BL Academy. Lives under Tools beside the Resource library: both are
      // learning surfaces, and it is a single page rather than a group.
      { label: "BL Academy", module: "bl_academy", to: "/academy", icon: GraduationCap },
      {
        label: "Resource",
        module: "resources",
        to: "/resources",
        icon: BookOpen,
        children: [
          { label: "Glossary", to: "/resources/glossary", icon: BookOpen },
          { label: "FAQ", to: "/resources/faq", icon: HelpCircle },
          { label: "Latest News & Articles", to: "/resources/news", icon: Newspaper },
          { label: "Press & Media", to: "/resources/press", icon: Megaphone },
        ],
      },
    ],
  },
  {
    // Single link to the roadmap page listing every upcoming module
    // (Data Room VDR, LOI Templates, Compliance Hub, Broker Messaging, …).
    heading: "New Modules",
    items: [
      { label: "Upcoming Modules", module: "upcoming_modules", to: "/buyer/upcoming-modules", icon: Sparkles, badge: "Soon", badgeTone: "new" },
    ],
  },
  {
    heading: "Account",
    items: [
      {
        label: "Settings",
        to: "/buyer/settings",
        icon: Settings,
        children: [
          { label: "My Profile", to: "/buyer/settings/profile", icon: UserRound },
          { label: "Password & Security", to: "/buyer/settings/security", icon: KeyRound },
          { label: "Email Notifications", to: "/buyer/settings/notifications", icon: Bell },
          { label: "Subscription Plan", to: "/buyer/settings/subscription", icon: CreditCard },
          { label: "Payment Method", to: "/buyer/settings/payment", icon: Wallet },
          { label: "Payment History", to: "/buyer/settings/history", icon: Receipt },
          { label: "Documents", to: "/buyer/settings/documents", icon: FileText },
        ],
      },
    ],
  },
];

export default function BuyerSidebar() {
  const { user } = useAuth();
  const [savedFilters, setSavedFilters] = useState<SavedFilter[]>([]);
  // Live total for the Available Listings badge: active listings + Coming Soon
  // pipeline deals — matching the "Total N listings" figure on the marketplace
  // page, which counts both tabs. (The Coming Soon NAV item is the modules
  // roadmap, so it carries no deal count.)
  const [activeCount, setActiveCount] = useState<number | null>(null);
  const [comingSoonCount, setComingSoonCount] = useState<number>(0);
  // The funding companies, one sidebar child each. Cached in lib/funding, so
  // this costs one request per session rather than one per sidebar mount.
  const [fundingPartners, setFundingPartners] = useState<FundingPartner[]>([]);
  // Unread advisor replies across the buyer's interest threads.
  const [interestUnread, setInterestUnread] = useState(0);

  useEffect(() => {
    if (!user) return;
    const load = () =>
      api.get<{ count: number }>("/interests/mine/unread-count")
        .then((d) => setInterestUnread(d.count))
        .catch(() => {});
    load();
    const id = window.setInterval(load, INTEREST_UNREAD_POLL_MS);
    // BuyerChats reports the live total as threads are opened, so the badge
    // clears the moment a reply is read rather than on the next poll.
    const onChange = (e: Event) => setInterestUnread((e as CustomEvent<number>).detail);
    window.addEventListener(INTEREST_UNREAD_EVENT, onChange);
    return () => {
      window.clearInterval(id);
      window.removeEventListener(INTEREST_UNREAD_EVENT, onChange);
    };
  }, [user]);

  const loadSaved = () => api.get<SavedFilter[]>("/saved-filters/").then(setSavedFilters).catch(() => {});

  useEffect(() => {
    if (!user) return;
    loadSaved();
    // Active count: status-counts is the cheap aggregate (open to every signed-in
    // user, tier-visibility aware). If it fails, fall back to the browse
    // endpoint's pagination total (page_size=1, we only want total_elements), the
    // same number the marketplace page falls back to for its "Active" tab.
    api.get<{ counts: Record<string, number> }>("/business-listings/status-counts")
      .then((data) => setActiveCount(data.counts["3"] ?? 0))
      .catch(() =>
        api.get<{ total_elements: number }>("/business-listings/?listing_status_id=3&page=1&page_size=1")
          .then((data) => setActiveCount(data.total_elements ?? 0))
          .catch(() => {}),
      );
    api.get<{ total_elements: number }>("/lead-pipeline/count")
      .then((data) => setComingSoonCount(data.total_elements ?? 0))
      .catch(() => {});
    // A failure just leaves the group without lender children this mount.
    loadFundingPartners().then(setFundingPartners).catch(() => {});
    window.addEventListener(SAVED_FILTERS_CHANGED_EVENT, loadSaved);
    return () => window.removeEventListener(SAVED_FILTERS_CHANGED_EVENT, loadSaved);
  }, [user]);

  const deleteSavedFilter = (id: string) => {
    api.delete(`/saved-filters/${id}`)
      .then(() => {
        loadSaved();
        window.dispatchEvent(new Event(SAVED_FILTERS_CHANGED_EVENT));
      })
      .catch(() => {});
  };

  // Inject the live count badges into the Discover items. Exact number (not
  // k-compacted) so it matches the page's "Total N listings" figure.
  const totalListings = (activeCount ?? 0) + comingSoonCount;
  const freeBuyer = isFreeBuyerUser(user);
  const operatorAccess = hasOperatorAccess(user);
  // Member-only nav — hidden from Basic (free) buyers until they upgrade.
  // "Resource" hides the whole Glossary/FAQ/News group: the children ride along
  // with their parent, and the API refuses a free buyer's feed request anyway.
  const MEMBER_ONLY_LABELS = ["AI Deal Matching", "Upcoming Modules", "Resource", "BL Academy", "Funding Marketplace"];
  // Operator-only nav — hidden from Basic AND Explorer buyers.
  const OPERATOR_ONLY_LABELS = ["Deal CRM"];
  const sections: NavSection[] = BASE_SECTIONS.map((s) =>
    s.heading === "Discover"
      ? {
          ...s,
          items: s.items.map((it) =>
            it.label === "Available Listings" && totalListings > 0
              ? { ...it, badge: totalListings.toLocaleString(), badgeTone: "count" as const }
              : it.label === "My Interests" && interestUnread > 0
                ? { ...it, badgeCount: interestUnread }
                : it,
          ),
        }
      : s,
  )
    .map((s) => ({
      ...s,
      items: s.items.filter((it) => {
        if (freeBuyer && MEMBER_ONLY_LABELS.includes(it.label)) return false;
        if (!operatorAccess && OPERATOR_ONLY_LABELS.includes(it.label)) return false;
        return true;
      }),
    }))
    .map((s) => ({
      ...s,
      // Funding companies are data, so the group is built at render time. With
      // none configured the group would expand to nothing, so it is dropped.
      items: s.items.flatMap((it) =>
        it.label !== FUNDING_LABEL
          ? [it]
          : fundingPartners.length === 0
            ? []
            : [{
                ...it,
                children: [
                  // The card grid. First child so the collapsed sidebar's
                  // deep-link (see AppSidebar) lands on the overview rather
                  // than on whichever lender happens to sort first.
                  { label: "All Lenders", to: "/buyer/funding", icon: LayoutGrid },
                  ...fundingPartners.map((p) => ({
                    label: p.name,
                    to: `/buyer/funding/${p.slug}`,
                    icon: Landmark,
                  })),
                ],
              }],
      ),
    }))
    .filter((s) => s.items.length > 0);
  if (savedFilters.length > 0) {
    sections.splice(1, 0, {
      heading: "Saved Filters",
      items: savedFilters.map((f) => ({
        label: f.name,
        to: `/buyer/home?savedFilter=${f.id}`,
        icon: Bookmark,
        onDelete: () => deleteSavedFilter(f.id),
        onRename: (name: string) => { renameSavedFilter(f.id, name).catch(() => {}); },
      })),
    });
  }

  // Free (Basic) buyers get a persistent upgrade CTA; members don't see it.
  const upgradeCta = isFreeBuyerUser(user)
    ? {
        to: "/choose-plan",
        label: "Upgrade plan",
        blurb: "Unlock advisor contact, exact locations, offers & favorites.",
      }
    : null;

  return <AppSidebar sections={sections} homeUrl="/buyer/dashboard" storageKey="buyerSidebarCollapsed" upgradeCta={upgradeCta} />;
}
